/**
 * IP-based Geolocation Utility
 *
 * Provides approximate user location based on IP address.
 * Uses free IP geolocation services without requiring API keys.
 *
 * @module utils/geolocation
 */

// Free IP geolocation services (no API key required) - Now used by backend only
const IP_SERVICES = [
  'http://ip-api.com/json/',              // Primary - HTTP to avoid 403, no rate limit for non-commercial
  'https://whois.pcomplett.com/api/ipgeo', // Backup - Very reliable, JSON API
];

/**
 * Get user's approximate location via backend API
 *
 * Uses server-side endpoint to avoid CSP/CORS issues.
 * Backend makes requests to IP geolocation services without browser restrictions.
 *
 * @returns {Promise<{lat: number, lon: number, city: string, country: string}|null>}
 * User's approximate location or null if failed
 *
 * @example
 * const location = await getIPLocation();
 * if (location) {
 *   console.log(`User is in ${location.city}, ${location.country}`);
 *   console.log(`Coordinates: ${location.lat}, ${location.lon}`);
 * }
 */
export async function getIPLocation() {
  try {
    const response = await fetch('/api/geolocation');

    if (!response.ok) {
      throw new Error(`Service returned ${response.status}`);
    }

    const data = await response.json();
    console.info('[Geolocation] Location detected:', data);
    return data;

  } catch (error) {
    console.warn('[Geolocation] Backend service failed:', error.message);
    return null;
  }
}

/**
 * Get default location (Brasília) when geolocation fails
 *
 * @returns {{lat: number, lon: number, city: string, country: string}}
 */
export function getDefaultLocation() {
  return {
    lat: -15.793889,  // Brasília latitude
    lon: -47.882778,  // Brasília longitude
    city: 'Brasília',
    country: 'Brazil',
  };
}

/**
 * Get user location with IP-based geolocation and fallback to default
 *
 * @returns {Promise<{lat: number, lon: number, city: string, country: string}>}
 * User's location (IP-based or default)
 *
 * @example
 * const location = await getUserLocation();
 * map.setView([location.lat, location.lon], 12);
 */
export async function getUserLocation() {
  const ipLocation = await getIPLocation();
  return ipLocation || getDefaultLocation();
}

/**
 * Request precise user location using browser Geolocation API
 *
 * Requires user permission. More accurate but requires explicit approval.
 *
 * @returns {Promise<{lat: number, lon: number}|null>}
 * Precise user location or null if denied/failed
 *
 * @example
 * const preciseLocation = await getBrowserGeolocation();
 * if (preciseLocation) {
 *   console.log('Precise location:', preciseLocation);
 * }
 */
export function getBrowserGeolocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn('[Geolocation] Browser geolocation not supported');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        };
        console.info('[Geolocation] Browser location detected:', location);
        resolve(location);
      },
      (error) => {
        console.warn('[Geolocation] Browser geolocation denied/failed:', error.message);
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes cache
      }
    );
  });
}
